import React from "react";
import {
  ActionIcon,
  Box,
  Image,
  Menu,
  Text,
  UnstyledButton,
} from "@mantine/core";
import {
  IconCalendar,
  IconCalendarWeek,
  IconChevronDown,
  IconFilter,
  IconLink,
  IconList,
  IconMenu2,
} from "@tabler/icons-react";
import { DESIGN_ICONS } from "../../../../../theme/designIcons";
import styles from "./MobileHeaderBand.module.css";

export type MobileView = "day" | "week" | "list";

export interface MobileHeaderBandProps {
  /** The viewed date ("Thu Oct 1, 2026"; week view: the week's range). */
  dateLabel: string;
  view: MobileView;
  /** Active filter groups (badge hidden at 0). */
  filterCount: number;
  bookingLink?: string | null;
  /** The booking link is on the clipboard (the surface shows a toast). */
  onLinkCopied: () => void;
  onMenu: () => void;
  /** Opens the date picker sheet. */
  onDate: () => void;
  onView: (view: MobileView) => void;
  onFilters: () => void;
  /** Walk-ins icon (prototype: icon only, Footprints) + waiting count. */
  walkIns?: { count: number; onOpen: () => void };
}

const WalkInsIcon = DESIGN_ICONS.Footprints;

const VIEW_ICON = {
  day: IconCalendar,
  week: IconCalendarWeek,
  list: IconList,
} as const;
const VIEW_LABEL = {
  day: "Day view",
  week: "Week view",
  list: "List view",
} as const;

/**
 * Two-row header band: menu · mark · link, then date · view menu · filters.
 */
export const MobileHeaderBand: React.FC<MobileHeaderBandProps> = ({
  dateLabel,
  view,
  filterCount,
  bookingLink,
  onLinkCopied,
  onMenu,
  onDate,
  onView,
  onFilters,
  walkIns,
}) => {
  const ViewIcon = VIEW_ICON[view];
  return (
    <Box className={styles.band} data-testid="mobile-header-band">
      <Box className={styles.row1}>
        <ActionIcon
          variant="subtle"
          className={styles.menuBtn}
          onClick={onMenu}
          aria-label="Menu"
        >
          <IconMenu2 size={18} stroke={1.8} />
        </ActionIcon>
        <Box className={styles.brand}>
          <Image
            src="/images/pagoda-logo.png"
            alt="Pagoda"
            className={styles.logo}
          />
        </Box>
        {bookingLink ? (
          <ActionIcon
            variant="transparent"
            className={styles.linkBtn}
            onClick={() => {
              navigator.clipboard
                ?.writeText(bookingLink)
                .then(onLinkCopied)
                .catch(() => {});
            }}
            aria-label="Copy booking link"
          >
            <IconLink size={17} stroke={1.8} />
          </ActionIcon>
        ) : (
          <Box className={styles.linkBtn} aria-hidden="true">
            <IconLink size={17} stroke={1.8} />
          </Box>
        )}
      </Box>

      <Box className={styles.row2}>
        <UnstyledButton
          className={styles.monthBtn}
          onClick={onDate}
          aria-label={`${dateLabel}, choose date`}
        >
          <Text span inherit>
            {dateLabel}
          </Text>
          <IconChevronDown size={16} stroke={1.8} />
        </UnstyledButton>
        <Box className={styles.spacer} />
        <Menu
          position="bottom-end"
          offset={4}
          classNames={{ dropdown: styles.viewMenu, item: styles.viewMenuItem }}
        >
          <Menu.Target>
            <UnstyledButton
              className={styles.viewBtn}
              aria-label={VIEW_LABEL[view]}
            >
              <ViewIcon size={17} stroke={1.8} />
              <IconChevronDown size={15} stroke={1.8} />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              data-current={view === "day"}
              leftSection={<IconCalendar size={15} stroke={1.8} />}
              onClick={() => onView("day")}
            >
              Day
            </Menu.Item>
            <Menu.Item
              data-current={view === "week"}
              leftSection={<IconCalendarWeek size={15} stroke={1.8} />}
              onClick={() => onView("week")}
            >
              Week
            </Menu.Item>
            <Menu.Item
              data-current={view === "list"}
              leftSection={<IconList size={15} stroke={1.8} />}
              onClick={() => onView("list")}
            >
              List
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
        {walkIns && (
          <Box className={styles.filterWrap}>
            <ActionIcon
              variant="default"
              className={styles.filterBtn}
              onClick={walkIns.onOpen}
              aria-label="Walk-ins"
            >
              <WalkInsIcon size={17} stroke={1.8} />
            </ActionIcon>
            {walkIns.count > 0 && (
              <Text span className={styles.filterBadge}>
                {walkIns.count}
              </Text>
            )}
          </Box>
        )}
        <Box className={styles.filterWrap}>
          <ActionIcon
            variant="default"
            className={styles.filterBtn}
            onClick={onFilters}
            aria-label="Filters"
          >
            <IconFilter size={17} stroke={1.8} />
          </ActionIcon>
          {filterCount > 0 && (
            <Text span className={styles.filterBadge}>
              {filterCount}
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};
